import { useEffect, useRef, useState } from 'react';

interface TurnTimerProps {
  secondsLeft: number | null;
  maxSeconds?: number;
  active?: boolean;
  size?: number;
  variant?: 'default' | 'hero' | 'ring' | 'seat';
  className?: string;
  /** Changes when turn moves — resets smooth countdown anchor */
  syncKey?: string;
}

/** Circular countdown on the active seat — smooth fractional progress ring */
export default function TurnTimer({
  secondsLeft,
  maxSeconds = 15,
  active,
  size = 72,
  variant = 'default',
  className = '',
  syncKey = '',
}: TurnTimerProps) {
  const [displayLeft, setDisplayLeft] = useState(0);
  const anchorRef = useRef<{ at: number; left: number } | null>(null);
  const syncKeyRef = useRef(syncKey);

  useEffect(() => {
    if (!active || secondsLeft == null || secondsLeft < 0) {
      anchorRef.current = null;
      setDisplayLeft(0);
      return;
    }

    const keyChanged = syncKey !== syncKeyRef.current;
    syncKeyRef.current = syncKey;

    if (keyChanged || anchorRef.current == null) {
      anchorRef.current = { at: Date.now(), left: secondsLeft };
      setDisplayLeft(secondsLeft);
      return;
    }

    const elapsed = (Date.now() - anchorRef.current.at) / 1000;
    const predicted = Math.max(0, anchorRef.current.left - elapsed);
    if (Math.abs(predicted - secondsLeft) > 0.35) {
      anchorRef.current = { at: Date.now(), left: secondsLeft };
      setDisplayLeft(secondsLeft);
    }
  }, [active, secondsLeft, syncKey]);

  useEffect(() => {
    if (!active || anchorRef.current == null) return;

    let raf = 0;
    const tick = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const elapsed = (Date.now() - anchor.at) / 1000;
      const left = Math.max(0, anchor.left - elapsed);
      setDisplayLeft(left);
      if (left > 0) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, syncKey, secondsLeft]);

  if (!active || secondsLeft == null) return null;

  const safeMax = Math.max(1, maxSeconds);
  const displaySeconds = Math.max(0, Math.ceil(displayLeft));
  const isHero = variant === 'hero';
  const isRing = variant === 'ring';
  const isSeat = variant === 'seat';
  const stroke = isRing ? 2.5 : 3.5;
  const viewSize = isRing ? 100 : size;
  const radius = isRing ? (viewSize - stroke * 3) / 2 : (size - stroke * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = viewSize / 2;
  const progress = Math.max(0, Math.min(1, displayLeft / safeMax));
  const offset = circumference * (1 - progress);
  const urgent = displaySeconds <= 5;
  const critical = displaySeconds <= 3;
  const color = urgent
    ? '#ef4444'
    : isRing || isHero
      ? '#22c55e'
      : '#D4AF37';
  const segment = circumference / 8;
  const placementClass = isRing
    ? 'tp-opponent-turn-ring'
    : isHero || isSeat
      ? 'tp-hero-turn-timer'
      : 'absolute left-1/2 top-1/2 z-[15] -translate-x-1/2 -translate-y-1/2';

  const trackStroke = isHero || isRing
    ? 'rgba(34, 197, 94, 0.32)'
    : isSeat
      ? 'rgba(212, 175, 55, 0.28)'
      : 'rgba(255,255,255,0.1)';

  return (
    <div
      className={`pointer-events-none ${placementClass} ${urgent ? 'tp-timer-urgent' : ''} ${critical ? 'tp-timer-critical' : ''} ${className}`}
      style={isRing ? undefined : { width: size, height: size }}
      aria-label={`${displaySeconds} seconds left`}
    >
      <svg
        width={isRing ? '100%' : size}
        height={isRing ? '100%' : size}
        viewBox={isRing ? `0 0 ${viewSize} ${viewSize}` : undefined}
        className="absolute inset-0 -rotate-90"
        preserveAspectRatio={isRing ? 'xMidYMid meet' : undefined}
      >
        {isHero && (
          <circle
            cx={center}
            cy={center}
            r={radius + 1.5}
            fill="none"
            stroke="rgba(212, 175, 55, 0.72)"
            strokeWidth={1.5}
          />
        )}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill={isHero || isSeat ? 'rgba(0, 0, 0, 0.55)' : 'none'}
          stroke="rgba(0,0,0,0.45)"
          strokeWidth={stroke + (isHero ? 0 : 0.5)}
        />
        {isHero && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={stroke}
            strokeDasharray={`${segment} ${segment}`}
          />
        )}
        {!isHero && !isRing && !isSeat && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth={stroke}
          />
        )}
        {(isSeat || isRing) && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={trackStroke}
            strokeWidth={stroke}
          />
        )}
        {isRing && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.35)"
            strokeWidth={stroke}
          />
        )}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="tp-turn-timer-progress"
        />
      </svg>
      {!isRing && variant !== 'seat' && (
        <span
          className={`absolute inset-0 flex items-center justify-center font-bold tabular-nums ${
            critical
              ? 'text-sm text-red-300'
              : urgent
                ? 'text-[11px] text-red-400'
                : isHero
                  ? 'text-xs text-white'
                  : isSeat
                    ? 'text-[11px] text-tp-gold'
                    : 'text-[11px] text-tp-gold'
          }`}
        >
          {displaySeconds}
        </span>
      )}
    </div>
  );
}
